import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from 'components/dialog/Modals'
import {
  addImageLibrary,
  clearSelectedImages,
  fetchModalImageList,
  hideImageLibraryModal,
  setName,
  setPage,
  setSize,
  setTabIndex,
  uploadImages,
} from 'features/image/imageLibrarySlice'
import FileInput from 'components/form/FileInput'
import ExifReader from 'exifreader'
import { getEventFiles, noop } from 'utils'
import { fail } from 'features/toast/Toast'
import { useDialog } from 'features/dialog/Dialog'
import Button from 'components/form/Button'
import {
  ImageLibraryList,
  ImageLibrayPendingList,
} from 'features/image/ImageLibraryList'
import { func } from 'prop-types'
import Limitations from 'features/Limitations'
import Tooltip from 'components/tooltip/Tooltip'
import preventDefault from 'prevent-default'
import ImageUploadGuidelines from 'features/image/ImageUploadGuidelines'
import Pagination from 'components/pagination/Pagination'
import PageSize from 'components/pagination/PageSize'
import Loading from 'components/loading/Loading'
import ImageDropArea from 'features/image/ImageDropArea'
import NoneAnchor from 'components/NoneAnchor'
import ImageGuideLink from './ImageGuideLink'

export const FILE_COUNT_LIMIT = 100
const IMAGE_ERRORS = {
  UNSUPPORTED_FILE_TYPE: '업로드 가능한 이미지 포맷이 아닙니다.',
  IMAGE_SIZE: '업로드 가능한 이미지 사이즈가 아닙니다.',
  FILE_SIZE: '업로드 가능한 이미지 용량이 아닙니다.',
  FILE_NAME: '업로드 가능한 이미지 파일명이 아닙니다.',
  UPLOAD_FAIL: '이미지 업로드에 실패했습니다.',
}

const ImageLibraryModal = ({ afterConfirm = noop }) => {
  const {
    isShow,
    uploadOnly,
    allowImageSizes,
    maxCount,
    images,
    selectedImages,
    progressCount,
    totalProgress,
    uploading,
    loading,
    libraryImages,
    tabIndex,
    pending,
    name,
    page,
    size,
    totalElements,
  } = useSelector(state => state.image.imageLibrary)
  const dispatch = useDispatch()
  const [dragOver, setDragOver] = useState(false)
  const [errors, setErrors] = useState([])
  const [searchText, setSearchText] = useState(name)
  const [ErrorMessageDialog, showErrorMessageDialog, hideErrorMessageDialog] =
    useDialog(false)
  const promiseRef = useRef(null)
  const scrollRef = useRef()
  useEffect(() => {
    if (errors.length > 0) {
      showErrorMessageDialog()
    } else {
      hideErrorMessageDialog()
    }
  }, [errors, showErrorMessageDialog, hideErrorMessageDialog])

  useEffect(() => {
    if (tabIndex !== 0) {
      dispatch(fetchModalImageList())
    }
  }, [dispatch, tabIndex, name, page, size])

  useEffect(() => {
    dispatch(clearSelectedImages())
    if (name === '') {
      setSearchText('')
    }
  }, [dispatch, name])

  const handleClose = () => {
    if (uploading) {
      fail('업로드 중인 파일이 있습니다.')
    } else {
      dispatch(hideImageLibraryModal())
    }
  }
  const readImage = file => {
    const reader = new FileReader()
    return new Promise(resolve => {
      reader.onload = () => {
        resolve(reader.result)
      }
      reader.readAsArrayBuffer(file)
    })
  }
  const handleFileChange = async inputFiles => {
    if (uploading) {
      fail('업로드 중인 파일이 있습니다.')
    } else {
      if (inputFiles.length + images.length > FILE_COUNT_LIMIT) {
        fail(
          `업로드 가능한 개수를 초과했습니다. 한번에 최대 ${FILE_COUNT_LIMIT}개까지 업로드 가능합니다.`
        )
      }
      const invalidImages = []
      const validImages = []
      const files = inputFiles.slice(0, FILE_COUNT_LIMIT - images.length)
      for (const file of files) {
        const extension = file.name.split('.').pop().toLowerCase()
        if (['png', 'jpeg', 'jpg'].includes(extension)) {
          const messages = []
          let imageWidth
          let imageHeight
          try {
            const arrayBuffer = await readImage(file)
            const tags = ExifReader.load(arrayBuffer)
            imageWidth =
              Object.prototype.hasOwnProperty.call(tags, 'Image Width') &&
              tags['Image Width'].value
            imageHeight =
              Object.prototype.hasOwnProperty.call(tags, 'Image Height') &&
              tags['Image Height'].value
          } catch (e) {
            messages.push(IMAGE_ERRORS.UPLOAD_FAIL)
          }

          if (messages.length === 0) {
            const allowImageSize = allowImageSizes.find(
              ({ width, height }) =>
                imageWidth === width && imageHeight === height
            )
            if (allowImageSize === undefined) {
              messages.push(IMAGE_ERRORS.IMAGE_SIZE)
            }
            const allowFileSize = allowImageSize
              ? allowImageSize.byte
              : Math.min(...allowImageSizes.map(({ byte }) => byte))
            if (file.size > allowFileSize) {
              messages.push(IMAGE_ERRORS.FILE_SIZE)
            }
            if (file.name.length > 1000) {
              messages.push(IMAGE_ERRORS.FILE_NAME)
            }
          }

          if (messages.length > 0) {
            invalidImages.push({
              name: file.name,
              messages,
            })
          } else {
            file.width = imageWidth
            file.height = imageHeight
            validImages.push(file)
          }
        } else {
          invalidImages.push({
            name: file.name,
            messages: [IMAGE_ERRORS.UNSUPPORTED_FILE_TYPE],
          })
        }
      }
      setErrors(invalidImages)
      if (invalidImages.length > 0) {
        showErrorMessageDialog()
      }
      if (validImages.length > 0) {
        promiseRef.current = dispatch(uploadImages(validImages))
      }
    }
  }
  const handleUploadCancel = () => {
    if (promiseRef.current) {
      promiseRef.current.abort()
    }
  }
  const handleSuccess = (confirmedImages, skipId = false) => {
    afterConfirm(
      confirmedImages.map(
        ({ id, url, width, height, name: imageName, size: imageSize }) => ({
          id: skipId ? null : id,
          url,
          width,
          height,
          name: imageName,
          size: imageSize,
        })
      )
    )
    dispatch(hideImageLibraryModal())
  }
  const handleConfirm = async () => {
    if (tabIndex === 0) {
      if (uploadOnly) {
        const { error, payload } = await dispatch(
          addImageLibrary(selectedImages)
        )
        if (error) {
          const { errorCode, errorMessage } = payload
          fail(
            errorCode === 112002
              ? `계정당 추가할 수 있는 이미지는 ${Limitations.MAX_IMAGE_LIBRARIES}개입니다.`
              : errorMessage
          )
        } else {
          handleSuccess(payload.dspImageDtos)
        }
      } else {
        handleSuccess(selectedImages, true)
      }
    } else {
      handleSuccess(selectedImages)
    }
  }
  const handleClear = () => {
    dispatch(clearSelectedImages())
  }
  const handleTabChange = idx => {
    if (tabIndex !== idx) {
      dispatch(setTabIndex(idx))
    }
  }
  const handlePageChange = v => dispatch(setPage(v))
  const handleSizeChange = v => dispatch(setSize(v))
  const handleDragEnter = event => {
    const hasFile = getEventFiles(event).length > 0
    if (hasFile && images.length > 0 && tabIndex === 0) {
      setDragOver(true)
      scrollRef.current?.scrollTo({ top: 0 })
    }
  }
  const handleDragOver = event => {
    event.preventDefault()
    const hasFile = getEventFiles(event).length > 0
    if (hasFile && images.length > 0 && tabIndex === 0) {
      setDragOver(true)
    }
  }

  return (
    <>
      <Modal className="imgmanage_layer" on={isShow}>
        <ConfirmModal
          title="이미지 등록하기"
          handleClose={handleClose}
          onConfirm={handleConfirm}
          confirmDisabled={loading || uploading || selectedImages.length === 0}
          footer={
            <span className="txt_select">
              <button type="button" className="btn_del" onClick={handleClear}>
                <span className="ico_comm ico_del">삭제</span>
              </button>
              선택<em className="num_select">{selectedImages.length}</em>/
              {maxCount}
            </span>
          }>
          <strong className="screen_out">소재 요소 가져오기 종류</strong>
          <ul className="tab_g8" role="tablist">
            <li
              role="presentation"
              className={classNames({ on: tabIndex === 0 })}>
              <NoneAnchor
                onClick={() => handleTabChange(0)}
                role="tab"
                className="link_tab">
                직접 업로드<span className="screen_out">선택됨</span>
              </NoneAnchor>
            </li>
            {!uploadOnly && (
              <>
                <li
                  role="presentation"
                  className={classNames({ on: tabIndex === 1 })}>
                  <NoneAnchor
                    onClick={() => handleTabChange(1)}
                    role="tab"
                    className="link_tab">
                    이미지 관리 불러오기
                  </NoneAnchor>
                </li>
                <li
                  role="presentation"
                  className={classNames({ on: tabIndex === 2 })}>
                  <NoneAnchor
                    onClick={() => handleTabChange(2)}
                    role="tab"
                    className="link_tab">
                    최근 사용한 이미지
                  </NoneAnchor>
                </li>
              </>
            )}
          </ul>
          <div className="wrap_material">
            <div className="group_info">
              <div className="guide_section">
                <div className="guide_item">
                  <Tooltip
                    position="bottom"
                    content={
                      <ImageUploadGuidelines
                        allowImageSizes={allowImageSizes}
                      />
                    }>
                    <NoneAnchor className="link_guide">
                      이미지 등록 가이드
                    </NoneAnchor>
                  </Tooltip>
                </div>
                <ImageGuideLink />
              </div>
              {tabIndex === 0 ? (
                <span className="box_inpfile">
                  <label
                    htmlFor="inpcustomFile"
                    className={classNames('btn_gs lab_file', {
                      in_active:
                        uploading || images.length === FILE_COUNT_LIMIT,
                    })}>
                    <span className="inner_g">
                      <span className="img_comm ico_add" />
                      파일 업로드
                    </span>
                  </label>
                  <FileInput
                    id="inpcustomFile"
                    className="inp_file"
                    clearFiles
                    multiple
                    accept=".png,.jpeg,.jpg"
                    onFileChange={handleFileChange}
                    disabled={uploading}
                  />
                </span>
              ) : (
                <div className="add_info">
                  <div className="group_option">
                    <div className="reform_search_complete">
                      <div className="reform_inner_complete">
                        <span className="box_inptxt search_inp2">
                          <span className="inner_inp">
                            <form
                              onSubmit={preventDefault(() =>
                                dispatch(setName(searchText))
                              )}>
                              <input
                                className="inp_txt"
                                type="text"
                                maxLength="25"
                                value={searchText}
                                placeholder="파일명 검색"
                                onChange={e => setSearchText(e.target.value)}
                              />
                              <button
                                type="submit"
                                className={classNames('btn_search', {
                                  in_active: pending,
                                })}
                                disabled={pending}>
                                <span className="ico_comm ico_search">
                                  검색
                                </span>
                              </button>
                            </form>
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div
              ref={scrollRef}
              className={classNames('group_material', {
                drop_area_all: dragOver,
                on_drop:
                  (tabIndex === 0 &&
                    uploading === false &&
                    images.length === 0) ||
                  dragOver,
              })}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}>
              {tabIndex === 0 ? (
                <ImageDropArea
                  setDragOver={setDragOver}
                  handleFileChange={handleFileChange}
                />
              ) : pending ? (
                <Loading />
              ) : (
                libraryImages.length === 0 && (
                  <div className="reform_box_nodata">
                    <p className="txt_nodata">
                      {tabIndex === 1
                        ? '이미지 관리 메뉴에 등록된 이미지가 없습니다.'
                        : '최근 사용한 이미지가 없습니다.'}
                    </p>
                  </div>
                )
              )}
              <div className="inner_material">
                <ul className="list_material">
                  <ImageLibraryList />
                  <ImageLibrayPendingList />
                </ul>
              </div>
            </div>
            {totalElements > 0 && tabIndex > 0 && (
              <div className="paging_wrap">
                <div className="inner_paging">
                  <Pagination
                    total={totalElements}
                    current={page}
                    size={size}
                    onChange={handlePageChange}
                  />
                  <PageSize
                    onSizeChange={s => {
                      handlePageChange(0)
                      handleSizeChange(s)
                    }}
                    size={size}
                    unit="개수"
                  />
                </div>
              </div>
            )}
          </div>
          {uploading && (
            <div className="layer_upload">
              <div className="inner_layer">
                <span className="txt_upload">
                  {progressCount}개 이미지 업로드 중
                </span>
                <span className="load_wrap">
                  <span className="load_bg">
                    <span
                      className="load_bar"
                      style={{ width: `${totalProgress}%` }}>
                      {totalProgress}%
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn_del"
                    onClick={handleUploadCancel}>
                    <span className="ico_comm ico_del">삭제</span>
                  </button>
                </span>
              </div>
            </div>
          )}
        </ConfirmModal>
      </Modal>
      <ErrorMessageDialog>
        <strong className="tit_layer">이미지 업로드 실패</strong>
        <p className="txt_layer">
          선택한 이미지 중 업로드할 수 없는 이미지가{' '}
          <span className="fc_emph">{errors.length}</span>개 있습니다.
          <br />
          사유 확인 후 다시 시도하세요.
        </p>
        <ul className="list_upload">
          {errors.map(({ name: fileName, messages }, index) => (
            <li key={index}>
              <strong className="tit_upload">{fileName}</strong>
              {messages.map((msg, i) => (
                <p key={`${index}_${i}`} className="desc_upload">
                  {msg}
                </p>
              ))}
            </li>
          ))}
        </ul>
        <div className="guide_item">
          <Tooltip
            position="bottom"
            content={
              <ImageUploadGuidelines allowImageSizes={allowImageSizes} />
            }>
            <NoneAnchor className="link_guide">
              업로드 가능 사이즈 및 용량
            </NoneAnchor>
          </Tooltip>
        </div>
        <div className="btn_group">
          <Button primary onClick={hideErrorMessageDialog}>
            확인
          </Button>
        </div>
      </ErrorMessageDialog>
    </>
  )
}
ImageLibraryModal.propTypes = {
  afterConfirm: func,
}
export default ImageLibraryModal
